import type { Locale } from '@/intl/types'
import { Banner } from '@sunwebgroup/uds-ui/banner'
import { Button } from '@sunwebgroup/uds-ui/button'
import { DateField } from '@sunwebgroup/uds-ui/fields/date-field'
import { TextField } from '@sunwebgroup/uds-ui/fields/text-field'
import { RadioButton } from '@sunwebgroup/uds-ui/radio-button'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import * as React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useIntl } from '@/intl'
import { SelectableListItem, SelectableRadioButtonGroup } from '@/scopes/checkout/components/SelectableListItem'
import { Voucher } from '@/scopes/common/components/Voucher/Voucher'
import ga4analyticsClient from '@/scopes/common/infrastructure/analytics/ga4analyticsClient'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { createLocation } from '@/scopes/common/infrastructure/dom/location'
import { getAmountValue, getSelectedAmount } from '@/scopes/payment/domain/adyen/policies/paymentSelectors'
import { usePaymentContext } from '@/scopes/payment/domain/PaymentContext'
import { PaymentOptions } from '@/scopes/payment/domain/types'
import { createVoucherActions } from '@/scopes/payment/domain/vouchers/actions'
import { isValidVoucherBirthdate, VOUCHER_EMAIL_PATTERN } from '@/scopes/payment/domain/vouchers/policies/voucherForm'
import { calculateVoucherRedemption } from '@/scopes/payment/domain/vouchers/policies/voucherRedemption'
import { getUsableOwnedVouchers } from '@/scopes/payment/domain/vouchers/selectors'
import {
  VoucherCannotBeUsedReason,
  VoucherCannotBeUsedReasonValues,
  VoucherFormValues,
} from '@/scopes/payment/domain/vouchers/types'
import { formatDate } from '@/scopes/payment/formatters'
import { voucherStore } from '@/scopes/payment/infrastructure/vouchers/store'
import { useVoucherStore } from '@/scopes/payment/infrastructure/vouchers/useVoucherStore'
import { createVoucherApiClient } from '@/scopes/payment/infrastructure/vouchers/voucherApiClient'
import styles from './VoucherPaymentDetails.module.css'
import { VoucherSelector } from './VoucherSelector'

const VOUCHER_REDEMPTION_OPTION = 'redeem'

interface VoucherPaymentDetailsProps {
  paymentOptions: PaymentOptions
  selectedAmountType: string
  customDepositAmount: number | null
}

function getCanNotBeUsedTranslationKeys(key: VoucherCannotBeUsedReason): string | undefined {
  const mapping: Record<VoucherCannotBeUsedReason, string> = {
    [VoucherCannotBeUsedReasonValues.EXPIRED]: 'payment_scope_voucher_expired',
    [VoucherCannotBeUsedReasonValues.IS_VOID]: 'payment_scope_voucher_void',
    [VoucherCannotBeUsedReasonValues.NO_BALANCE]: 'payment_scope_voucher_no_balance',
  }
  return mapping[key]
}

export function VoucherPaymentDetails({ paymentOptions, selectedAmountType, customDepositAmount }: VoucherPaymentDetailsProps) {
  const intl = useIntl()
  const currentLocale = intl.locale as Locale

  const { bookingNumber, contextItemId, groupUserId, scope } = usePaymentContext()
  const analyticsClient = ga4analyticsClient
  const state = useVoucherStore(value => value)
  const dateFormat = useConfiguration('dateFormat')
  const [showManualForm, setShowManualForm] = React.useState(false)
  const actions = React.useMemo(() => createVoucherActions({
    apiClient: createVoucherApiClient({ contextItemId, groupUserId }),
    store: voucherStore,
    location: createLocation(),
    bookingNumber,
    groupUserId,
    paymentOptions,
    selectedAmountType,
    customDepositAmount,
    dateFormat,
    scope,
    analyticsClient,
  }), [analyticsClient, bookingNumber, contextItemId, customDepositAmount, dateFormat, groupUserId, paymentOptions, scope, selectedAmountType])
  const required = intl.formatMessage({ id: 'payment_scope_voucher_required_error' })
  const invalid = intl.formatMessage({ id: 'payment_scope_voucher_invalid_error' })
  const invalidEmail = intl.formatMessage({ id: 'payment_scope_voucher_email_invalid_error' })
  const invalidBirthdate = intl.formatMessage({ id: 'payment_scope_voucher_birthdate_invalid_error' })
  const selectedAmount = getSelectedAmount(paymentOptions.paymentAmounts, selectedAmountType)
  const paymentAmount = getAmountValue(selectedAmount, customDepositAmount)
  const hasUsableOwnedVouchers = getUsableOwnedVouchers(paymentOptions.vouchers).length > 0
  const { control, formState: { errors }, handleSubmit, register, reset } = useForm<VoucherFormValues>({
    defaultValues: {
      voucherCode: '',
      email: '',
      birthdate: '',
    },
  })

  function voucherFlowCanceled() {
    actions.voucherFlowCanceled()
    reset()
  }

  function availableVouchersViewed() {
    actions.availableVouchersViewed()
    reset()
    setShowManualForm(false)
  }

  if (state.screen === 'unusable' && state.voucher) {
    return (
      <div className={styles.container}>
        <Banner color="warning" size="S" hasIcon contentIsCentered>
          <Banner.Text>
            {state.voucher.cannotBeUsedReasons.map((reason) => {
              const cannotBeUsedTextKey = getCanNotBeUsedTranslationKeys(reason as VoucherCannotBeUsedReason) || ''
              return intl.formatMessage({ id: cannotBeUsedTextKey })
            }).join(' ')}
          </Banner.Text>
        </Banner>
        <Button type="button" variant="secondary" onClick={voucherFlowCanceled}>
          {intl.formatMessage({ id: 'payment_scope_voucher_cancel' })}
        </Button>
      </div>
    )
  }

  if (state.screen === 'options' && state.voucher) {
    const redemption = paymentAmount !== null ? calculateVoucherRedemption(state.voucher.amount, paymentAmount) : null

    return (
      <div className={styles.container}>
        <Voucher
          amount={state.voucher.amount}
          expirationDate={formatDate(state.voucher.expirationDate, currentLocale)}
          ariaLabel={intl.formatMessage({ id: 'payment_scope_voucher_card_label' })}
        />
        <Typography variant="body-s-regular-tight">
          {intl.formatMessage({ id: 'payment_scope_voucher_terms' })}
        </Typography>
        {paymentAmount && (
          <Typography variant="body-s-bold-tight">
            {intl.formatMessage({ id: 'payment_scope_voucher_total' }, { amount: intl.formatCurrency(paymentAmount) })}
          </Typography>
        )}
        {redemption && (
          <SelectableRadioButtonGroup
            aria-label={intl.formatMessage({ id: 'payment_scope_voucher_options_label' })}
            value={VOUCHER_REDEMPTION_OPTION}
            onValueChange={() => {}}
          >
            <SelectableListItem value={VOUCHER_REDEMPTION_OPTION} className={styles.option}>
              <div className={styles.contentOption}>
                <div>
                  <RadioButton value={VOUCHER_REDEMPTION_OPTION} onClick={event => event.stopPropagation()} />
                </div>
                <div>
                  <Typography variant="body-s-regular-tight">
                    {intl.formatMessage({ id: 'payment_scope_voucher_redeem' })}
                  </Typography>
                </div>
                <div className={styles.contentOptionTrailing}>
                  <Typography variant="body-s-regular-tight">{intl.formatCurrency(redemption.amountToPay)}</Typography>
                </div>
              </div>
            </SelectableListItem>
          </SelectableRadioButtonGroup>
        )}
        {state.applyError && <Typography role="alert">{invalid}</Typography>}
        <div className={styles.actions}>
          <Button type="button" variant="secondary" onClick={voucherFlowCanceled}>
            {intl.formatMessage({ id: 'payment_scope_voucher_cancel' })}
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={!redemption || state.applyLoading}
            isLoading={state.applyLoading}
            onClick={() => void actions.voucherPaymentRequested()}
          >
            {intl.formatMessage({ id: 'payment_scope_voucher_apply' })}
          </Button>
        </div>
      </div>
    )
  }

  if (state.screen === 'redeem' && !showManualForm && paymentOptions.vouchers) {
    return (
      <VoucherSelector
        vouchers={paymentOptions.vouchers}
        onSelect={voucher => actions.ownedVoucherSelected(voucher)}
        onAddVoucherClick={() => {
          actions.manualVoucherFlowOpened()
          setShowManualForm(true)
        }}
      />
    )
  }

  return (
    <div className={styles.container}>
      <Typography variant="body-s-regular-tight">{intl.formatMessage({ id: 'payment_scope_voucher_description' })}</Typography>
      {state.genericError && <Typography role="alert" className={styles.error}>{invalid}</Typography>}
      <form
        className={styles.form}
        noValidate
        onSubmit={handleSubmit(values => void actions.voucherCheckRequested(values), () => actions.voucherFormValidationFailed())}
      >
        <TextField
          label={intl.formatMessage({ id: 'payment_scope_voucher_code_label' })}
          status={errors.voucherCode ? 'error' : 'default'}
          feedbackText={errors.voucherCode?.message}
          {...register('voucherCode', {
            required,
            onChange: actions.voucherFormChanged,
          })}
        />
        <TextField
          label={intl.formatMessage({ id: 'payment_scope_voucher_email_label' })}
          status={errors.email ? 'error' : 'default'}
          feedbackText={errors.email?.message}
          inputProps={{ type: 'email', autoComplete: 'email' }}
          {...register('email', {
            required,
            pattern: {
              value: VOUCHER_EMAIL_PATTERN,
              message: invalidEmail,
            },
            onChange: actions.voucherFormChanged,
          })}
        />
        <Controller
          name="birthdate"
          control={control}
          rules={{
            required,
            validate: value => isValidVoucherBirthdate(value, dateFormat) || invalidBirthdate,
          }}
          render={({ field }) => (
            <DateField
              ref={field.ref}
              name={field.name}
              label={intl.formatMessage({ id: 'payment_scope_voucher_birthdate_label' })}
              value={field.value}
              onChange={(value) => {
                field.onChange(value)
                actions.voucherFormChanged()
              }}
              onBlur={field.onBlur}
              dateFormat={dateFormat}
              autoComplete="bday"
              status={errors.birthdate ? 'error' : 'default'}
              feedbackText={errors.birthdate?.message}
              helpText={dateFormat}
            />
          )}
        />
        <div className={styles.actions}>
          {showManualForm && hasUsableOwnedVouchers && (
            <Button type="button" variant="transparentPrimary" onClick={availableVouchersViewed}>
              {intl.formatMessage({ id: 'payment_scope_voucher_view_available_button' })}
            </Button>
          )}
          <Button type="submit" variant="primary" disabled={state.checkLoading} isLoading={state.checkLoading}>{intl.formatMessage({ id: 'payment_scope_voucher_submit' })}</Button>
        </div>
      </form>
    </div>
  )
}
